<template>
  <v-container fluid class="pa-0 mt-4">
    <CalendarNavigation
      v-model:week-offset="weekOffset"
      :selected-year="selectedYear"
      :turnosChanged="false"
      :showSaveButton="false"
      @yearChange="handleYearChange"
      @weekChanged="handleWeekChange"
    />

    <div class="d-flex justify-center px-4 my-2">
      <v-tooltip location="top" text="Vacaciones">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            prepend-icon="mdi-beach"
            color="primary"
            variant="outlined"
            @click="handleShowVacaciones"
          >
            Vacaciones
          </v-btn>
        </template>
      </v-tooltip>
    </div>

    <VacacionesModal
      v-model="showVacationsDialog"
      :vacaciones="vacacionesPorMes"
      :year="selectedYear"
      :selected-user="authStore.user"
    />

    <!-- <UserCards
      :users="[authStore.user]"
      :selected-user-id="authStore.user.id"
      :schedules="schedules"
      :days="visibleDays"
      @user-selected="handleUserSelected"
    /> -->
    <UserShiftsTable
      v-if="authStore.user && weekOffset > 0"
      v-model:week-offset="weekOffset"
      :selected-year="selectedYear"
      :time-slots="turnosStore.timeSlots"
      :schedules="schedules"
      :puestoId="authStore.user.puesto"
      :users="turnosStore.users"
      :readOnly="true"
    />
  </v-container>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useTurnosStore } from '@/stores/turnosStore'
import { useAuthStore } from '@/stores/authStore'
import { getISOWeekNumber, getTodayDate } from '@/utils/helpers'
import CalendarNavigation from './subcomponents/CalendarNavigation.vue'
import UserShiftsTable from './subcomponents/UserShiftsTable.vue'
import VacacionesModal from './subcomponents/VacacionesModal.vue'
// import UserCards from './subcomponents/UserCards.vue'

const turnosStore = useTurnosStore()
const authStore = useAuthStore()
const weekOffset = ref(0)

// Calcular el año ISO inicial
const initialToday = getTodayDate()
const initialTempDate = new Date(initialToday)
initialTempDate.setDate(
  initialTempDate.getDate() + 3 - ((initialTempDate.getDay() + 6) % 7)
)
const selectedYear = ref(initialTempDate.getFullYear())
const schedules = ref({})
const showVacationsDialog = ref(false)
const vacacionesPorMes = ref(Array(12).fill(0)) // Array con los días de vacaciones por mes

function getMondayOfWeek(weekNumber, year) {
  // Asegurarnos de que weekNumber sea válido
  const validWeekNumber = Math.max(1, Math.min(53, weekNumber || 1))

  // Calcular el primer jueves del año (4 de enero siempre está en la semana 1 ISO)
  const firstThursday = new Date(year, 0, 4)
  // Ajustar al jueves de esa semana usando la fórmula ISO
  firstThursday.setDate(
    firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7)
  )

  // Calcular el lunes de la primera semana (3 días antes del jueves)
  const firstMonday = new Date(firstThursday)
  firstMonday.setDate(firstThursday.getDate() - 3)

  // Calcular el lunes de la semana objetivo
  const targetMonday = new Date(firstMonday)
  targetMonday.setDate(firstMonday.getDate() + (validWeekNumber - 1) * 7)
  console.log(
    'getMondayOfWeek - año:',
    year,
    'semana:',
    validWeekNumber,
    'lunes:',
    targetMonday
  )

  return targetMonday
}

// const visibleDays = computed(() => {
//   const firstDay = getMondayOfWeek(weekOffset.value || 1, selectedYear.value)
//   const days = []

//   for (let i = 0; i < 28; i++) {
//     const day = new Date(firstDay)
//     day.setDate(firstDay.getDate() + i)
//     days.push(day)
//   }

//   return days
// })

// Methods
const handleYearChange = async (year) => {
  console.log('Año cambiado a:', year)
  selectedYear.value = year

  const today = getTodayDate()

  // Calcular el año ISO de la fecha de prueba
  const tempDate = new Date(today)
  tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
  const currentISOYear = tempDate.getFullYear()

  // Si el año es el actual (año ISO), mantener la semana actual
  // Si no, empezar desde la semana 1
  if (year === currentISOYear) {
    weekOffset.value = getISOWeekNumber(today)
  } else {
    weekOffset.value = 1 // Resetear a la primera semana del año
  }

  // Calcular el primer día del año seleccionado con la semana correspondiente
  const firstDay = getFirstDayOfYear(year, weekOffset.value)
  const fecha = getDateKey(firstDay)

  try {
    const userId = authStore.user.id
    schedules.value = await turnosStore.fetchUserTurnos(userId, fecha)
  } catch (error) {
    console.error('Error loading user turnos:', error)
  }
}

const getFirstDayOfYear = (year, week) => {
  // Obtener el 4 de enero del año (siempre está en la primera semana ISO)
  const firstWeekDate = new Date(year, 0, 4)
  // Obtener el día de la semana (0-6, donde 0 es domingo)
  const firstWeekDay = firstWeekDate.getDay() || 7
  // Calcular el primer lunes del año
  const firstMonday = new Date(year, 0, 4 - firstWeekDay + 1)

  // Ajustar a la semana deseada
  const targetDate = new Date(firstMonday)
  targetDate.setDate(firstMonday.getDate() + (week - 1) * 7)

  return targetDate
}

const handleWeekChange = async (newWeek) => {
  console.log('Semana cambiada a:', newWeek)
  weekOffset.value = newWeek

  // Usar el mismo cálculo del primer día para la nueva semana
  const firstDay = getFirstDayOfYear(selectedYear.value, newWeek)
  const fecha = getDateKey(firstDay)

  try {
    const userId = authStore.user.id
    schedules.value = await turnosStore.fetchUserTurnos(userId, fecha)
  } catch (error) {
    console.error('Error loading user turnos:', error)
  }
}

const loadUserTurnos = async () => {
  try {
    console.log(
      'Cargando turnos para año:',
      selectedYear.value,
      'semana:',
      weekOffset.value
    )
    const firstDay = getMondayOfWeek(weekOffset.value, selectedYear.value)
    const fecha = getDateKey(firstDay)
    const userId = authStore.user.id
    schedules.value = await turnosStore.fetchUserTurnos(userId, fecha)
  } catch (error) {
    console.error('Error loading user turnos:', error)
  }
}

const getDateKey = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

onMounted(async () => {
  // Esperar al siguiente ciclo de renderizado
  await nextTick()

  // Comprobar si el usuario está disponible
  if (authStore.user) {
    turnosStore.setTimeSlotsPuesto(authStore.user.puesto)
    const today = getTodayDate()

    // Calcular el año ISO de la semana
    const tempDate = new Date(today)
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
    selectedYear.value = tempDate.getFullYear()
    weekOffset.value = Math.max(1, getISOWeekNumber(today))

    console.log(
      '🧪 UserShifts onMounted - Año ISO:',
      selectedYear.value,
      '| Semana:',
      weekOffset.value
    )

    await loadUserTurnos()
  } else {
    console.warn('Usuario no disponible después de nextTick')
    // Valores por defecto para evitar otros errores
    const today = getTodayDate()

    // Calcular el año ISO de la semana
    const tempDate = new Date(today)
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
    selectedYear.value = tempDate.getFullYear()
    weekOffset.value = Math.max(1, getISOWeekNumber(today))
  }
})

const handleShowVacaciones = async () => {
  if (!authStore.user) return
  showVacationsDialog.value = true
  try {
    const response = await turnosStore.getVacacionesPorMes(
      authStore.user.id,
      selectedYear.value
    )
    vacacionesPorMes.value = response.vacaciones_por_mes
  } catch (error) {
    console.error('Error al obtener vacaciones:', error)
  }
}
</script>
